import { Dispatch, SetStateAction, useState } from 'react'
import { FLOWCODE_ROOT } from '@dtx-company/true-common/src/constants/root'
import { LandingPageDestination } from '@dtx-company/inter-app/src/types/flowcode'
import { MAX_SLUG_LENGTH } from '@app/page/src/constants'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { SLUG_RESERVED } from '@dtx-company/flow-codegen/src/page/queries'
import { SlugReservedQuery } from '@dtx-company/flow-codegen/src/page/generated.types'
import { SuccessPageDomainVariants } from '../../../CodeCreationSuccess/Success.types'
import { UpdateFlowcodeVariablesType } from '@app/code/src/types/flowcode.types'
import { Url } from 'next/dist/shared/lib/router/router'
import { cleanSlug } from '@app/page/src/utils/slug'
import { createRedirectValue } from '@app/pre-build/src/components/OnboardingFlows/GenericFp/ClaimPage/ClaimPage.utils'
import {
  getBatchIdFromFlowcodeId,
  getFlowcodeId,
  updateBatchWithFile,
  updateFlowcode
} from '../../../../utils/flowcode'
import { getFile } from '@app/code/src/utils/getFile'
import {
  getSocialsLandingPageDestination,
  getWidgetLinkForCode
} from '../CombinedFunnelMobile.utils'
import { pageGqlFetcher } from '@dtx-company/inter-app/src/services/gqlFetcher'
import { resolveHref } from 'next/dist/client/resolve-href'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useAuthCache } from '@app/authentication/AuthCache/hooks'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCreateCode } from '../../CreateFlowcodeForm/CreateFlowcodeFormDesktop/CreateFlowcodeFormDesktop.hooks'
import { useCreateFlowcodeState } from '@app/code/src/hooks/useCreateFlowcodeState'
import { useCreateFlowcodeStateActions } from '../../CreateFlowcodeForm/CreateFlowcodeForm.hooks'
import { useCreateMobileCodeArgs } from '../CombinedFunnelMobile'
import { useCreatePage } from '@dtx-company/inter-app/src/hooks/useCreatePage'
import {
  useCurrentPage,
  useHasUserReachedPageLimit
} from '@dtx-company/inter-app/src/redux/slices/utils/currentPage'
import Router, { useRouter } from 'next/router'
import events from '@dtx-company/inter-app/src/event-tracking/events/homepage'

export function useCombinedFunnelMobileAuthState(
  variant?: string | null,
  initialIsSignUp = true,
  redirectDirectlyToCode?: boolean
): {
  setIsSignUp: Dispatch<SetStateAction<boolean>>
  isSignUp: boolean
  handleGenerateCode: () => Promise<void>
  handSubmitBtnEvents: () => void
  onClickGoogleBtn: (googleLoginUrl: string) => Promise<void>
  handleFacebookBtnEvents: () => void
} {
  const { jwt, pageLimit } = useAuthState()
  const [isSignUp, setIsSignUp] = useState(initialIsSignUp)
  const createFlowcode = useCreateCode()
  const { setLoadingCreateFlowcode, setShortURL } = useCreateFlowcodeStateActions()
  const { loadingCreateFlowcode } = useCreateFlowcodeState()
  const createPage = useCreatePage()
  const currentPage = useCurrentPage()
  const linksCount = currentPage?.links?.length ?? 0
  let slugName = ''
  const userHasReachedPageLimit = useHasUserReachedPageLimit(pageLimit)
  const handleUpdateCodeRedirect = async (
    updatedFlowcodeVariables: UpdateFlowcodeVariablesType
  ): Promise<void> => {
    try {
      const updateFlowcodeResponse = await updateFlowcode(updatedFlowcodeVariables)
      if (!updateFlowcodeResponse.updateBatchLink.ok) {
        throw new Error('Error updating flowcode')
      }
    } catch (e) {
      sendErrorNotification(e)
      console.error(
        `error updating combined mobile funnel code redirect. BatchId - ${updatedFlowcodeVariables.batchId}`
      )
    }
  }

  const handleCreateFlowPage = async (batchId: string): Promise<void> => {
    slugName = cleanSlug(`${batchId}-${Date.now()}`) as string
    const { slugReserved } = await pageGqlFetcher<SlugReservedQuery>(
      SLUG_RESERVED,
      { slug: slugName },
      jwt?.token
    )
    if (slugReserved) {
      sendErrorNotification('Name is already taken')
      throw new Error(`Combined mobile funnel fc+fp slugname already taken. BatchId - ${batchId}`)
    }
    if (slugName.length > MAX_SLUG_LENGTH) {
      sendErrorNotification(`Name cannot be longer than ${MAX_SLUG_LENGTH} characters`)
      throw new Error(`Combined mobile funnel fc+fp slugname already taken. BatchId - ${batchId}`)
    }
    await createPage({
      slugName,
      pageType: currentPage?.pageType ?? undefined
    })
    await handleUpdateCodeRedirect({
      batchId,
      batchInput: {
        nickname: slugName
      },
      linkInput: {
        redirectType: LandingPageDestination.FLOWPAGE,
        redirectValue: createRedirectValue(slugName)
      }
    })
    events.Flowcode_Combined_Funnel_Mobile_Homepage_Created_Page({
      variant
    })
  }

  const handleGenerateCode = async (): Promise<void> => {
    try {
      const createFlowcodeOnly = linksCount === 1
      isSignUp
        ? events.Flowcode_Combined_Funnel_Mobile_Homepage_Created_Account({ variant })
        : events.Flowcode_Combined_Funnel_Mobile_Homepage_SignedIn({ variant })
      if (!currentPage) throw new Error('Flowpage not saved.')
      if (loadingCreateFlowcode) {
        console.warn('Generate code request already in progress')
        return
      }
      // do not create code if page limit reached
      if (!createFlowcodeOnly && userHasReachedPageLimit) {
        throw new Error('Error creating page. Page limit has been reached.')
      }
      setLoadingCreateFlowcode(true)
      const createFlowcodeResponse = await createFlowcode()
      const shortUrl = createFlowcodeResponse.flowcodeBatch.shortUrl
      setShortURL(shortUrl)
      const flowcodeId = getFlowcodeId(shortUrl)
      const batchId = getBatchIdFromFlowcodeId(flowcodeId)
      const domain = createFlowcodeOnly
        ? SuccessPageDomainVariants.FLOWCODE
        : SuccessPageDomainVariants.FLOWPAGE

      if (createFlowcodeOnly) {
        if (!currentPage.links?.length) throw new Error('Destination link not found')
        const provider = currentPage.links ? currentPage.links[0]?.provider : ''
        const currentPageType = currentPage.links ? currentPage.links[0]?.type : ''
        const destination = getSocialsLandingPageDestination(provider, currentPageType)
        const destinationLink =
          provider === 'widget'
            ? getWidgetLinkForCode(currentPage.links[0])
            : currentPage.links[0]?.actionData.link
        if (
          destination === LandingPageDestination.FILE ||
          destination === LandingPageDestination.PDF
        ) {
          const file = await getFile(destinationLink, `file-${batchId}`)
          await updateBatchWithFile({
            file,
            batchId
          })
        } else {
          await handleUpdateCodeRedirect({
            batchId,
            batchInput: {
              nickname: ''
            },
            linkInput: {
              redirectType: destination,
              redirectValue: destinationLink
            }
          })
          events.Flowcode_Combined_Funnel_Mobile_Homepage_Created_Code({
            variant
          })
        }
      } else {
        await handleCreateFlowPage(batchId)
      }
      const redirectPath: Url = redirectDirectlyToCode
        ? {
            pathname: Routes.CODE,
            query: {
              batchId
            }
          }
        : `${Routes.CREATE_CODE_SUCCESS}?batchId=${batchId}&v=${variant ?? ''}&d=${domain ?? ''}${
            !createFlowcodeOnly ? `&slug=${slugName}` : ''
          }`
      const [, resolvedUrl] = resolveHref(Router, redirectPath, true)
      window.location.href = `${FLOWCODE_ROOT}${resolvedUrl}` //used on www and app subdomains, can't use router.push
    } catch (e) {
      console.error('error creating combined mobile funnel fc + fp', e)
      sendErrorNotification(
        e.message || 'Something went wrong. Please try again or email support@flowcode.com.'
      )
    } finally {
      setLoadingCreateFlowcode(false)
    }
  }
  const handSubmitBtnEvents = (): void => {
    isSignUp
      ? events.Flowcode_Combined_Funnel_Mobile_Homepage_Clicked_Sign_Up_With_Email({
          variant
        })
      : events.Flowcode_Combined_Funnel_Mobile_Homepage_Clicked_Sign_In_With_Email({
          variant
        })
  }

  const locgFormValues = useCreateMobileCodeArgs()
  const queryParams = useRouter().query
  const authCache = useAuthCache()

  const onClickGoogleBtn = async (googleLoginUrl: string): Promise<void> => {
    isSignUp
      ? events.Flowcode_Combined_Funnel_Mobile_Homepage_Clicked_Sign_Up_With_Google({ variant })
      : events.Flowcode_Combined_Funnel_Mobile_Homepage_Clicked_Sign_In_With_Google({ variant })

    await Promise.all([
      authCache?.queryParams.set(queryParams),
      authCache?.locgFormValues.set(locgFormValues)
    ])
    window.location.href = googleLoginUrl
  }
  const handleFacebookBtnEvents = (): void => {
    isSignUp
      ? events.Flowcode_Combined_Funnel_Mobile_Homepage_Clicked_Sign_Up_With_Facebook({ variant })
      : events.Flowcode_Combined_Funnel_Mobile_Homepage_Clicked_Sign_In_With_Facebook({ variant })
  }
  return {
    setIsSignUp,
    isSignUp,
    handleGenerateCode,
    handSubmitBtnEvents,
    onClickGoogleBtn,
    handleFacebookBtnEvents
  }
}
